import { BBox, Vec2 } from './math';
import type { TextAnchor, TextEntity, TextVerticalAlign } from './types';

/** Rough per-glyph advance factors (fraction of cap height) for a technical sans font. */
const WIDTH_FACTORS: Record<string, number> = {
  ' ': 0.42,
  '.': 0.30,
  ',': 0.30,
  ':': 0.34,
  ';': 0.34,
  "'": 0.24,
  '"': 0.40,
  '!': 0.34,
  '|': 0.28,
  '/': 0.44,
  '\\': 0.44,
  '-': 0.42,
  '+': 0.62,
  '=': 0.62,
  '<': 0.62,
  '>': 0.62,
  '~': 0.62,
  '(': 0.38,
  ')': 0.38,
  '[': 0.34,
  ']': 0.34,
  '{': 0.38,
  '}': 0.38,
  '%': 0.78,
  '#': 0.66,
  '@': 0.86,
  '&': 0.70,
  '*': 0.62,
  '0': 0.60,
  '1': 0.60,
  '2': 0.60,
  '3': 0.60,
  '4': 0.60,
  '5': 0.60,
  '6': 0.60,
  '7': 0.60,
  '8': 0.60,
  '9': 0.60,
  M: 0.86,
  W: 0.86,
  I: 0.30,
  J: 0.48,
  L: 0.52,
  T: 0.60,
  Y: 0.66,
  f: 0.42,
  i: 0.28,
  j: 0.28,
  l: 0.28,
  m: 0.90,
  r: 0.42,
  t: 0.42,
  w: 0.78,
};

function glyphWidth(ch: string): number {
  return WIDTH_FACTORS[ch] ?? 0.62;
}

/** Approximate advance width of single-line text, in millimetres. */
export function measureTextWidth(
  text: string,
  height: number,
  widthFactor = 1,
  tracking = 0.04,
): number {
  let units = 0;
  let count = 0;
  for (const ch of text) {
    units += glyphWidth(ch) + tracking;
    count += 1;
  }
  if (count > 0) units -= tracking;
  return units * height * widthFactor;
}

export function textLineHeight(entity: TextEntity): number {
  return entity.height * 1.4;
}

export function textDescent(entity: TextEntity): number {
  return entity.height * 0.22;
}

/** Renderer-facing description of a piece of text placed in the world. */
export interface TextSpec {
  text: string;
  position: Vec2;
  /** Cap height in millimetres. */
  height: number;
  rotation: number;
  anchor: TextAnchor;
  valign: TextVerticalAlign;
  widthFactor: number;
}

export function textSpecBBox(spec: TextSpec): BBox {
  const width = measureTextWidth(spec.text, spec.height, spec.widthFactor);
  const descent = spec.height * 0.22;

  let x0: number;
  switch (spec.anchor) {
    case 'center':
      x0 = -width / 2;
      break;
    case 'right':
      x0 = -width;
      break;
    default:
      x0 = 0;
      break;
  }

  let y0: number;
  switch (spec.valign) {
    case 'bottom':
      y0 = 0;
      break;
    case 'middle':
      y0 = -spec.height / 2;
      break;
    case 'top':
      y0 = -spec.height;
      break;
    default:
      y0 = -descent;
      break;
  }

  const local = [
    Vec2.of(x0, y0),
    Vec2.of(x0 + width, y0),
    Vec2.of(x0 + width, y0 + spec.height + descent),
    Vec2.of(x0, y0 + spec.height + descent),
  ];
  const world =
    spec.rotation === 0
      ? local.map((p) => p.add(spec.position))
      : local.map((p) => p.rotate(spec.rotation).add(spec.position));
  return BBox.fromPoints(world);
}
